import styled from "styled-components";
import React from "react";
import { Colors } from "../Colors";

const clamp = (value: number, min: number, max: number) =>
	Math.max(min, Math.min(max, value));

const toPercent = (value: number, min: number, max: number) => {
	if (max === min) {
		return 0;
	}

	return ((value - min) / (max - min)) * 100;
};

const Styles = {
	Frame: styled.label`
		display: grid;
		grid-template-columns: 1fr auto;
		align-items: center;
		gap: 10px;
		width: 100%;
		color: ${Colors.raw.terminal_light};
		font-family: "Courier New", monospace;
		user-select: none;
	`,

	Input: styled.input<{ $percent: number }>`
		width: 100%;
		height: 6px;
		appearance: none;
		-webkit-appearance: none;
		border-radius: 999px;
		outline: none;
		cursor: pointer;

		background: ${({ $percent }) => `
			linear-gradient(
				90deg,
				${Colors.raw.neon_orange} 0%,
				${Colors.raw.neon_orange} ${$percent}%,
				rgba(255, 255, 255, 0.14) ${$percent}%,
				rgba(255, 255, 255, 0.14) 100%
			)
		`};

		box-shadow:
			inset 0 0 6px rgba(0, 0, 0, 0.65),
			0 0 8px rgba(255, 122, 0, 0.16);

		&::-webkit-slider-thumb {
			appearance: none;
			-webkit-appearance: none;
			width: 16px;
			height: 16px;
			border-radius: 50%;
			border: 1px solid ${Colors.raw.terminal_light};
			background: ${Colors.raw.neon_orange};
			box-shadow:
				0 0 8px rgba(255, 122, 0, 0.65),
				0 0 14px rgba(255, 122, 0, 0.28);
			cursor: pointer;
		}

		&::-moz-range-thumb {
			width: 16px;
			height: 16px;
			border-radius: 50%;
			border: 1px solid ${Colors.raw.terminal_light};
			background: ${Colors.raw.neon_orange};
			box-shadow:
				0 0 8px rgba(255, 122, 0, 0.65),
				0 0 14px rgba(255, 122, 0, 0.28);
			cursor: pointer;
		}

		&::-moz-range-track {
			height: 6px;
			border-radius: 999px;
			background: transparent;
		}

		&:disabled {
			opacity: 0.45;
			cursor: not-allowed;
		}

		&:disabled::-webkit-slider-thumb {
			cursor: not-allowed;
		}

		&:disabled::-moz-range-thumb {
			cursor: not-allowed;
		}
	`,

	Value: styled.div`
		min-width: 42px;
		text-align: right;
		color: ${Colors.raw.terminal_light};
		font-size: 12px;
		font-weight: 700;
		text-shadow: 0 0 8px rgba(255, 122, 0, 0.35);
	`,
};

type VolumeSetProps = {
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	step?: number;
	label?: string;
	disabled?: boolean;
};

export const VolumeSet = ({
							  value,
							  onChange,
							  min = 0,
							  max = 100,
							  step = 1,
							  label = "Volume",
							  disabled,
						  }: VolumeSetProps) => {
	const safeValue = clamp(value, min, max);
	const percent = clamp(toPercent(safeValue, min, max), 0, 100);

	return (
		<Styles.Frame>
			<Styles.Input
				type="range"
				min={min}
				max={max}
				step={step}
				value={safeValue}
				disabled={disabled}
				aria-label={label}
				$percent={percent}
				onChange={event => onChange(Number(event.target.value))}
			/>

			<Styles.Value>{Math.round(percent)}%</Styles.Value>
		</Styles.Frame>
	);
};